import { Options as HighchartsOptions } from 'highcharts';

// eslint-disable-next-line import-x/no-cycle
import {
  CalculatedWorkContributionData,
  EstimatedServiceLevelPerQueue,
  ForecastType,
  PtoRequest,
  Queue,
  Schedule,
  ScheduleDaySettings,
  ScheduleElementTag,
  ScheduleInfo,
  ScheduledAgent,
  Skill,
  StateEnum,
} from '../../graphql/components';
import { CommonScheduleElement, SortOrderType, Values } from '../../types';
import { Agent } from '../Agents/types';
// eslint-disable-next-line import-x/no-cycle
import { CalculatedScheduleDataPerQueue } from './utils/mapChartData';

type QueueContributionPerInterval = Record<number, number>;
/**
 * Example of usage:
 * {
 *   queueId1: { timestamp: 1, timestamp2: 0.5, timestamp3: 0 },
 *   queueId2: { timestamp: 0, timestamp2: 1, timestamp3: 2 }
 * }
 */
export type TransformedStaffingPerQueue = Record<string, QueueContributionPerInterval>;
export type ScheduleWithoutQueuesStaffing = Omit<Schedule, 'scheduleQueues'>;

export interface ScheduleState {
  initialStaffingPerQueue?: TransformedStaffingPerQueue;
  isDirty: boolean;
  allAgentsAreActive: boolean;
  schedule?: ScheduleWithoutQueuesStaffing;
  scheduledAgents?: ScheduledAgent[];
  usedScheduleElements?: ChartScheduleElement[];
  scheduleDaysSettings?: ScheduleDaySettings[];
  viewedTimezone?: string;
  autoScheduleSelectedRange?: AutoScheduleSelectedRange;
  autoScheduleOptions?: AutoScheduleOptions;
  usePrecalculatedQueuesStaffing?: boolean;
}

export interface FormValues {
  id?: string;
  name?: string;
  queueIds?: string[];
  schedulingStartDateTime?: Date | null;
  schedulingEndDateTime?: Date | null;
  cloneScheduleId?: string;
  availableSeats?: string;
  state?: StateEnum;
  timezone?: string;
}

export interface LocationState {
  initialScheduleInfo?: ScheduleInfo;
  formValues?: FormValues;
  oldFormValues?: FormValues;
  scheduleState?: ScheduleState;
  forceRefetchSchedule?: boolean;
}

export interface ScheduleElement {
  color: {
    blue: number;
    green: number;
    red: number;
  };
  defaultDuration: number;
  defaultDropElementWidth: number;
  id: string;
  name: string;
  nameSize: number;
  paidTimeOff: boolean;
  paid: boolean;
  pinnable: boolean;
  tags: ScheduleElementTag[];
  shift: boolean;
  productive: boolean;
}

export interface ChartScheduleElement extends CommonScheduleElement {
  agentId: string;
  pinned: boolean;
  pinnable: boolean;
  selected: boolean;
  wholeDayElement: boolean;
  dayIndexes: number[];
  shift: boolean;
  id: string;
}

export type PTORequestPartial = Pick<PtoRequest, 'id'> & {
  agentId: PtoRequest['agent']['id'];
  startDateTime: number;
  endDateTime: number;
};

export type ProductionChartScheduleElement = ChartScheduleElement & {
  queueIds: string[];
  sourceScheduleId?: string;
  ptoRequestId?: string;
};

export const SchedulingAction = {
  INITIAL: 'INITIAL',
  COMPOSE_DATA: 'COMPOSE_DATA',
  ADD_ELEMENT: 'ADD_ELEMENT',
  REMOVE_ELEMENTS: 'REMOVE_ELEMENTS',
  SELECT_AGENTS: 'SELECT_AGENTS',
  REMOVE_AGENTS: 'REMOVE_AGENTS',
  UPDATE_DATE: 'UPDATE_DATE',
  UPDATE_ELEMENTS: 'UPDATE_ELEMENTS',
  COPY_SCHEDULE: 'COPY_SCHEDULE',
  CLONE_SCHEDULE: 'CLONE_SCHEDULE',
  PASTE_SCHEDULE: 'PASTE_SCHEDULE',
  CLEAR_SCHEDULE: 'CLEAR_SCHEDULE',
  SELECT_ALL_AGENT_SCHEDULE: 'SELECT_ALL_AGENT_SCHEDULE',
  UPDATE_DAY_SETTING: 'UPDATE_DAY_SETTING',
  UPDATE_AGENT_DAY_SETTING: 'UPDATE_AGENT_DAY_SETTING',
  SHOW_ALL_AGENTS: 'SHOW_ALL_AGENTS',
  HIDE_NON_WORKING_AGENTS: 'HIDE_NON_WORKING_AGENTS',
  REWIND_SCHEDULE: 'REWIND_SCHEDULE',
  RESET_DIRTY_FLAG: 'RESET_DIRTY_FLAG',
  LOAD_WORK_CONTRIBUTION: 'LOAD_WORK_CONTRIBUTION',
  LOAD_WORK_CONTRIBUTION_FAILED: 'LOAD_WORK_CONTRIBUTION_FAILED',
  CALCULATE_WORK_CONTRIBUTION: 'CALCULATE_WORK_CONTRIBUTION',
  LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING: 'LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING',
  LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING_FAILED: 'LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING_FAILED',
  RESTORE_PRODUCTION_ELEMENTS_QUEUES_STAFFING: 'RESTORE_PRODUCTION_ELEMENTS_QUEUES_STAFFING',
  LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION: 'LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION',
  LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION_FAILED: 'LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION_FAILED',
  CALCULATE_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION: 'CALCULATE_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION',
  LOAD_ESTIMATED_SERVICE_LEVEL: 'LOAD_ESTIMATED_SERVICE_LEVEL',
  LOAD_ESTIMATED_SERVICE_LEVEL_FAILED: 'LOAD_ESTIMATED_SERVICE_LEVEL_FAILED',
  CALCULATE_ESTIMATED_SERVICE_LEVEL: 'CALCULATE_ESTIMATED_SERVICE_LEVEL',
  SET_LOCKED_AGENTS_IDS: 'SET_LOCKED_AGENTS_IDS',
  LOAD_FORECAST_DATA: 'LOAD_FORECAST_DATA',
  LOAD_FORECAST_DATA_FAILED: 'LOAD_FORECAST_DATA_FAILED',
  RESTORE_FORECAST_DATA: 'RESTORE_FORECAST_DATA',
  CHANGE_TIMEZONE: 'CHANGE_TIMEZONE',
  PIN_ALL_SCHEDULE_ELEMENTS: 'PIN_ALL_SCHEDULE_ELEMENTS',
  PIN_ELEMENTS_IN_CURRENT_DAY: 'PIN_ELEMENTS_IN_CURRENT_DAY',
  PIN_SELECTED_ELEMENTS: 'PIN_SELECTED_ELEMENTS',
  RELOAD_SCHEDULE_AFTER_AUTO_SCHEDULE_FINISHED: 'RELOAD_SCHEDULE_AFTER_AUTO_SCHEDULE_FINISHED',
  SET_AUTO_SCHEDULE_OPTIONS: 'SET_AUTO_SCHEDULE_OPTIONS',
  SET_AUTO_SCHEDULE_SELECTED_RANGE: 'SET_AUTO_SCHEDULE_SELECTED_RANGE',
  RESET_AUTO_SCHEDULE_OPTIONS_AND_SELECTED_RANGE: 'RESET_AUTO_SCHEDULE_OPTIONS_AND_SELECTED_RANGE',
  SET_LOADING_PRODUCTION_SCHEDULE_ELEMENTS: 'SET_LOADING_PRODUCTION_SCHEDULE_ELEMENTS',
  RESOLVE_CONFLICTS_ON_ELEMENTS: 'RESOLVE_CONFLICTS_ON_ELEMENTS',
  RESTORE_PRODUCTION_SCHEDULE_ELEMENTS: 'RESTORE_PRODUCTION_SCHEDULE_ELEMENTS',
  TOGGLE_SELECT_SCHEDULE_ELEMENTS_STATE: 'TOGGLE_SELECT_SCHEDULE_ELEMENTS_STATE',
  UNPIN_ALL_SCHEDULE_ELEMENTS: 'UNPIN_ALL_SCHEDULE_ELEMENTS',
  UNPIN_ELEMENTS_IN_CURRENT_DAY: 'UNPIN_ELEMENTS_IN_CURRENT_DAY',
  UNPIN_SELECTED_ELEMENTS: 'UNPIN_SELECTED_ELEMENTS',
  RELOAD_CHART_DATA: 'RELOAD_CHART_DATA',
  CHANGE_SOURCE_OF_FORECAST: 'CHANGE_SOURCE_OF_FORECAST',
} as const;

type WorkContributionPerQueue = {
  agentsContribution: Record<string, number>;
  traffic: number;
};

export type ScheduleElementsContributionPerQueue = Record<string, Record<string, Record<string, number>>>;

export type TransformedWorkContribution = Record<string, Record<string, WorkContributionPerQueue>>;
export type TransformedProductionElementsWorkContribution = Record<
  string,
  Record<string, Record<string, WorkContributionPerQueue>>
>;
export type TransformProductionElementsWorkContributionData = {
  data: CalculatedWorkContributionData[];
  scheduleId: string;
};
export type TransformedProductionForecast = Record<string, Record<string, number>>;
export interface SchedulingChartProps {
  dataPerQueue?: CalculatedScheduleDataPerQueue;
  plotBandsData: Array<{ start: number; end: number; queueIds: string[] }>;
  yAxisTicks: number[];
}

/**
 * contains every interval that is missing forecast
 * usage: { queueId: { [timestamp] = true }}
 */
export type MissingForecastPerQueue = Record<string, Record<number, boolean>>;

export interface AgentDaySettings {
  agentId: string;
  remote: boolean;
}

export interface DaySettings {
  dayIndex: number;
  agents: Record<string, AgentDaySettings>;
  hideNonWorkingAgents: boolean;
}

export interface SchedulingStateClipboard {
  isPasteScheduleActive: boolean;
  copiedScheduleElements: ChartScheduleElement[];
  agentDaySettings: Partial<Omit<AgentDaySettings, 'agentId'>>;
}

// Record<agentId, scheduleElement || undefined>
type SurroundingElementsOfAgents = Record<string, ChartScheduleElement | undefined>;

export type NearestSurroundingDaysElements = {
  nextDay: SurroundingElementsOfAgents;
  previousDay: SurroundingElementsOfAgents;
};

export type ElementIdsPerAgent = {
  pinned: string[];
  pinnable: string[];
  selected: string[];
  total: string[];
};

export type AutoScheduleOptions = {
  weightBreakSpread: number;
  weightChannelSpread: number;
  weightMaxUnderStaff: number;
};

export type AutoScheduleSelectedRange = {
  endDateTime: number;
  startDateTime: number;
  strictSelectedRange: boolean;
};

export const SchedulingChartType = {
  ...ForecastType,
  SLA: 'SERVICE_LEVEL_AGREEMENT',
} as const;

interface CommonSchedulingState {
  autoScheduleOptions: AutoScheduleOptions;
  autoScheduleSelectedRange: AutoScheduleSelectedRange;
  daysSettings: Record<number, DaySettings>;
  chartState: HighchartsOptions;
  forecastDataType: ForecastType;
  schedulingChartType: Values<typeof SchedulingChartType>;
  initialQueuesStaffingData: TransformedStaffingPerQueue;
  inactiveAgentIdsWithScheduleElement: string[];
  isDirty: boolean;
  nearestSurroundingDaysElements: NearestSurroundingDaysElements;
  nearestSurroundingDaysProductionElements: NearestSurroundingDaysElements;
  notAssignedAgentIdsWithScheduleElement: string[]; // This is no longer used - we no longer counts with contribution of not assigned agents
  pickedDayIndex: number;
  pinnedScheduleElementsIds: string[];
  pinnedUsedScheduleElementsIds: string[];
  productionScheduleElementsWithinCurrentDay: ProductionChartScheduleElement[];
  queuesStaffingOfProductionElementsData: TransformedStaffingPerQueue;
  selectedAgentIds: string[];
  selectedAgentIdsSorted: string[];
  selectedScheduleElementsIds: string[];
  usedScheduleElements: ChartScheduleElement[];
  usedScheduleElementsWithinCurrentDay: ChartScheduleElement[];
  usePrecalculatedQueuesStaffing: boolean;
  viewedTimezone: string;
  visibleAgentIds: string[];
  visibleSupervisedAgentIds: string[];
  visibleAgentIdsInViewPort: Record<string, boolean>;
}

export type QueueWithColor = Queue & { color: string };
export type QueuesWithColors = Record<string, QueueWithColor>;

export type ProductionScheduleElementsCoverage = {
  agentIds: string[];
  endDateTime: number;
  queueIds: string[];
  scheduleId: string;
  startDateTime: number;
};
export interface SchedulingState extends CommonSchedulingState {
  agents: Record<string, Agent>;
  supervisedAgentsRecordObj: Record<string, Agent>;
  clipboard: SchedulingStateClipboard;
  days: Date[];
  daysInScheduleTimezone: Date[];
  filter: {
    sortByFields: Array<keyof Agent>;
    sortOrder: SortOrderType;
  };
  estimatedServiceLevelPerQueue?: Record<string, EstimatedServiceLevelPerQueue>;
  forecast: TransformedProductionForecast;
  missingForecastIntervalsPerQueue: MissingForecastPerQueue;
  missingOrIncompleteForecastQueueIds: string[];
  isCalculatingWorkContributionData: boolean;
  isCalculatingWorkContributionOfProductionElements: boolean;
  isLoadingEstimatedServiceLevel: boolean;
  isLoadingQueuesStaffingOfProductionElements: boolean;
  isLoadingForecast: boolean;
  isLoadingProductionScheduleElements: boolean;
  lockedAgentsIds: Record<string, boolean>;
  productionElementsWorkContributionData: TransformedProductionElementsWorkContribution;
  productionScheduleElements: ProductionChartScheduleElement[];
  productionScheduleElementsMissingQueueIds: string[];
  productionScheduleElementsCoverage: ProductionScheduleElementsCoverage[];
  productiveElementsIds: string[];
  queueIds: string[];
  queues: QueuesWithColors;
  scheduleElements: ScheduleElement[];
  schedulingChartState: SchedulingChartProps & {
    scheduleElementsContributionPerQueue: ScheduleElementsContributionPerQueue;
  };
  scheduleEndTime: number;
  scheduleStartTime: number;
  scheduleHistory: ScheduleHistory;
  schedulingAction: Values<typeof SchedulingAction>;
  skills: Record<string, Skill>;
  timezone: string;
  workContributionData: TransformedWorkContribution;
}

export interface SchedulingGanttChartDragState {
  shouldAllowDrop: boolean;
}

export interface SchedulingStateToHistory extends CommonSchedulingState {
  actionType?: ActionType;
}

export interface ScheduleHistory {
  positionInHistory: number;
  schedulingStateHistory: SchedulingStateToHistory[];
}

export interface ActionType {
  type: string;
  snackBarMessage?: boolean;
}

export interface AddElementPayload {
  newElement: ChartScheduleElement;
}

export interface ComposeDataPayload {
  activeAgents: Agent[];
  supervisedAgentsRecordObj: Record<string, Agent>;
  agentsRecordObj: Record<string, Agent>;
  allAgentIdsSorted: string[];
  daysSettings: ScheduleDaySettings[];
  deletedAgentsWithScheduleElement: Agent[];
  inactiveAgentIdsWithScheduleElement: string[];
  includeNotAssignedAgents: boolean;
  initialStaffingPerQueue?: TransformedStaffingPerQueue;
  lockedAgentsIds: Record<string, boolean>;
  missingQueuesData: Record<string, Queue>;
  notAssignedAgents: Agent[];
  productionScheduleElements: ProductionChartScheduleElement[];
  productionScheduleElementsCoverage: ProductionScheduleElementsCoverage[];
  productionScheduleElementsMissingQueueIds: string[];
  queueIds: string[];
  queues: Queue[];
  selectedAgentIds: string[];
  shouldPreloadAllAgents: boolean;
  scheduledAgents: ScheduledAgent[];
  scheduleElements: ScheduleElement[];
  skills: Skill[];
  usedScheduleElements: ChartScheduleElement[];
  usePrecalculatedQueuesStaffing: boolean;
}
